import React from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CheckCircle, Clock, Edit, Eye, AlertTriangle, Lock } from 'lucide-react';

interface RoleBasedSectionWrapperProps {
  children: React.ReactNode;
  sectionTitle: string;
  sectionDescription: string;
  userRole: string;
  canEdit: boolean;
  canView: boolean;
  isCompleted?: boolean;
  isRequired?: boolean;
  completedBy?: string;
  completedAt?: string;
  onSectionAction?: () => void;
  requiresApprovalAction?: boolean; // For approval sections that need action but aren't "editable"
  canApprove?: boolean;
}

export const RoleBasedSectionWrapper: React.FC<RoleBasedSectionWrapperProps> = ({
  children,
  sectionTitle,
  sectionDescription,
  userRole,
  canEdit,
  canView,
  isCompleted = false,
  isRequired = false,
  completedBy,
  completedAt,
  onSectionAction,
  requiresApprovalAction = false,
  canApprove = false
}) => {
  const getSectionStatus = () => {
    if (!canView) return 'blocked';
    if (isCompleted) return 'completed';
    // For approval sections that need action from the user
    if (requiresApprovalAction && canApprove && !isCompleted) return 'waiting';
    if (canEdit && isRequired) return 'waiting';
    if (canEdit) return 'available';
    return 'readonly';
  };

  const getStatusIcon = () => {
    const status = getSectionStatus();
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-[hsl(var(--section-completed))]" />;
      case 'waiting':
        return <Clock className="h-4 w-4 text-[hsl(var(--action-required))]" />;
      case 'available':
        return <Edit className="h-4 w-4 text-[hsl(var(--section-available))]" />;
      case 'readonly':
        return <Eye className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />;
      case 'blocked':
        return <Lock className="h-4 w-4 text-[hsl(var(--section-blocked))]" />;
      default:
        return <AlertTriangle className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />;
    }
  };

  const getStatusBadge = () => {
    const status = getSectionStatus();
    switch (status) {
      case 'completed':
        return <Badge className="action-completed">✓ Completed</Badge>;
      case 'waiting':
        return <Badge className="action-required">⏳ Action Required</Badge>;
      case 'available':
        return <Badge className="action-optional">📝 Available</Badge>;
      case 'readonly':
        return <Badge variant="outline">👁️ View Only</Badge>;
      case 'blocked':
        return <Badge variant="destructive">🔒 Access Restricted</Badge>;
      default:
        return <Badge variant="secondary">Unknown</Badge>;
    }
  };

  const getActionButton = () => {
    const status = getSectionStatus();
    if (!onSectionAction) return null;

    switch (status) {
      case 'waiting':
        return (
          <Button 
            onClick={onSectionAction} 
            className="action-required"
            size="sm"
          >
            Complete This Section
          </Button>
        );
      case 'available':
        return (
          <Button 
            onClick={onSectionAction} 
            className="action-optional"
            size="sm"
          >
            Edit Section
          </Button>
        );
      case 'completed':
        return (
          <Button 
            onClick={onSectionAction} 
            variant="outline"
            size="sm"
          >
            Review
          </Button>
        );
      default:
        return null;
    }
  };

  const getSectionClassName = () => {
    const status = getSectionStatus();
    const baseClass = "transition-all duration-200";
    
    switch (status) {
      case 'completed':
        return `${baseClass} section-completed`;
      case 'waiting':
        return `${baseClass} section-waiting`;
      case 'available':
        return `${baseClass} section-available`;
      case 'readonly':
        return `${baseClass} section-readonly`;
      case 'blocked':
        return `${baseClass} section-blocked`;
      default:
        return baseClass;
    }
  };

  return (
    <Card className={getSectionClassName()}>
      <div className="px-4 py-5 md:px-6 md:py-6">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-4">
          <div className="flex items-start gap-3">
            {getStatusIcon()}
            <div>
              <h3 className="text-lg md:text-xl font-semibold flex items-center gap-2">
                {sectionTitle}
                {isRequired && <span className="text-[hsl(var(--action-required))]">*</span>}
              </h3>
              <p className="text-sm md:text-base text-muted-foreground">{sectionDescription}</p>
              {completedBy && completedAt && (
                <p className="text-xs md:text-sm text-muted-foreground mt-1">
                  Completed by {completedBy} on {new Date(completedAt).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {getStatusBadge()}
            {getActionButton()}
          </div>
        </div>

        {/* Section Content */}
        <div className={!canEdit ? 'opacity-75' : ''}>
          {children}
        </div>

        {/* Role-specific Guidance */}
        {getSectionStatus() === 'waiting' && (
          <div className="mt-4 p-3 bg-[hsl(var(--action-required))]/10 rounded-md border border-[hsl(var(--action-required))]/20">
            <p className="text-sm font-medium text-[hsl(var(--action-required))]">
              👋 This section requires your attention as a {userRole}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Complete this section to help move the game preparation forward.
            </p>
          </div>
        )}
      </div>
    </Card>
  );
};